import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Alert,
    Snackbar,
    CircularProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import CreateTimecardDialog from './CreateTimecardDialog';
import EditTimecardDialog from './EditTimecardDialog';
import { useAuth } from '../../context/AuthContext';
import { getCurrentUser, getUserPtoHours, getUserProjectHours } from '../../services/userService';
import PTODialog from './PTODialog';
import ProjectHoursDialog from './ProjectHoursDialog';
import { 
    getCurrentWeekTimecards, 
    createTimecard, 
    deleteTimecard,
    updateTimecard,
    getUncertifiedTimecards
} from '../../services/timecardService';
import { parseUTCDate, getWeekDates } from '../../utils/dateUtils';
import { useIOSScrollFix } from '../../hooks/useIOSScrollFix';

const Timecard = () => {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedTimecard, setSelectedTimecard] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPTODialogOpen, setIsPTODialogOpen] = useState(false);
    const [ptoData, setPTOData] = useState(null);
    const [isProjectHoursDialogOpen, setIsProjectHoursDialogOpen] = useState(false);
    const [projectHoursData, setProjectHoursData] = useState(null);
    const [hasUncertifiedTimecards, setHasUncertifiedTimecards] = useState(false);
    const [weekInfo, setWeekInfo] = useState(null);
    const [success, setSuccess] = useState('');
    const [showMonthlyReminder, setShowMonthlyReminder] = useState(false);
    const [monthlyReminderData, setMonthlyReminderData] = useState(null);
    const { user: currentUser } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // Use iOS-specific scroll fix hook
    const { forceIOSScrollReset } = useIOSScrollFix();

    // Helper function to determine if notification banner should be shown
    // Shows from Friday through Monday 8:00 AM to remind users before deadline
    const shouldShowNotificationBanner = () => {
        // TESTING: Uncomment the line below to force banner display for testing
        // return true;
        
        const now = new Date();
        const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 5 = Friday, 6 = Saturday
        const currentHour = now.getHours(); // 0-23
        
        // Show on Friday (day 5)
        if (currentDay === 5) {
            return true;
        }
        
        // Show on Saturday (day 6) and Sunday (day 0)
        if (currentDay === 6 || currentDay === 0) {
            return true;
        }
        
        // Show on Monday (day 1) before 8:00 AM
        if (currentDay === 1 && currentHour < 8) {
            return true;
        }
        
        return false;
    };

    // Helper function to determine if monthly project hours reminder should be shown
    // Shows on the first day of the month for 24 hours
    const shouldShowMonthlyProjectHoursReminder = () => {
        // TESTING: Uncomment the line below to force monthly banner display for testing
        // return true;
        
        const now = new Date();
        const currentDay = now.getDate(); // 1-31
        
        // Show on the first day of the month
        return currentDay === 1;
    };

    const fetchCurrentWeekTimecards = useCallback(async () => {
        try {
            const data = await getCurrentWeekTimecards();
            
            // Get the current week's range
            const now = new Date();
            console.log('fetchCurrentWeekTimecards - now:', now.toISOString());
            
            // Use getWeekDates to get consistent week info
            const weekInfo = getWeekDates(now);
            console.log('fetchCurrentWeekTimecards - weekInfo:', {
                weekNum: weekInfo.weekNum,
                dateRange: weekInfo.dateRange,
                startDate: weekInfo.startDate.toISOString(),
                endDate: weekInfo.endDate.toISOString(),
                weekDates: weekInfo.weekDates.map(d => ({
                    date: d.full.toISOString(),
                    display: d.display,
                    dayName: d.dayName
                }))
            });
            
            console.log('fetchCurrentWeekTimecards - weekInfo:', {
                weekNum: weekInfo.weekNum,
                dateRange: weekInfo.dateRange,
                startDate: weekInfo.startDate.toISOString(),
                endDate: weekInfo.endDate.toISOString(),
                weekDates: weekInfo.weekDates.map(d => ({
                    date: d.full.toISOString(),
                    day: d.full.getUTCDay(),
                    display: d.display,
                    dayName: d.dayName
                }))
            });
            
            setWeekInfo(weekInfo);

            setTimecards(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching timecards:', err);
            setError(err.message);
            setTimecards([]);
            // Set default week info even if there's an error
            const now = new Date();
            const weekInfo = getWeekDates(now);
            console.log('fetchCurrentWeekTimecards error - weekInfo:', {
                weekNum: weekInfo.weekNum,
                dateRange: weekInfo.dateRange,
                startDate: weekInfo.startDate.toISOString(),
                endDate: weekInfo.endDate.toISOString(),
                weekDates: weekInfo.weekDates.map(d => ({
                    date: d.full.toISOString(),
                    display: d.display,
                    dayName: d.dayName
                }))
            });
            setWeekInfo(weekInfo);
        }
    }, []);
    
    const checkUncertifiedTimecards = useCallback(async () => {
        try {
            setLoading(true);
            const uncertifiedTimecards = await getUncertifiedTimecards();
            
            // Set flag for warning message and show uncertified timecards if they exist
            const hasUncertified = uncertifiedTimecards.some(tc => {
                // Check if any days are not certified or not submitted
                const hasUncertifiedOrUnsubmittedDays = tc.days.some(d => 
                    !d.certified || !d.submitted
                );
                
                // Check timecard status
                const needsAttention = tc.status === 'Uncertified' || 
                    tc.status === 'Partially Certified';
                
                return hasUncertifiedOrUnsubmittedDays || needsAttention;
            });
            setHasUncertifiedTimecards(hasUncertified);
            
            // Always show uncertified timecards (or empty array if none)
            if (uncertifiedTimecards.length > 0) {
                const firstTimecard = uncertifiedTimecards[0];
                const weekInfo = getWeekDates(parseUTCDate(firstTimecard.startDate));
                setWeekInfo(weekInfo);
            } else {
                // Set current week info even if no timecards
                const now = new Date();
                const weekInfo = getWeekDates(now);
                setWeekInfo(weekInfo);
            }
            setTimecards(uncertifiedTimecards);
            setError(null);
        } catch (err) {
            console.error('Error checking uncertified timecards:', err);
            setError(err.message);
            await fetchCurrentWeekTimecards();
        } finally {
            setLoading(false);
        }
    }, [fetchCurrentWeekTimecards]);
    
    useEffect(() => {
        checkUncertifiedTimecards();
    }, [checkUncertifiedTimecards]);

    // Check for monthly project hours reminder
    useEffect(() => {
        const checkMonthlyReminder = async () => {
            if (shouldShowMonthlyProjectHoursReminder() && currentUser) {
                try {
                    // Get current user data
                    const userData = await getCurrentUser();
                    
                    // Get calculated project hours for the current year
                    const projectHours = await getUserProjectHours(userData._id);
                    
                    setMonthlyReminderData({
                        ytdUsed: projectHours.ytdUsed,
                        remaining: projectHours.remaining
                    });
                    setShowMonthlyReminder(true);
                } catch (err) {
                    console.error('Error fetching project hours for monthly reminder:', err);
                    // Don't show reminder if we can't fetch the data
                    setShowMonthlyReminder(false);
                    setMonthlyReminderData(null);
                }
            } else {
                setShowMonthlyReminder(false);
                setMonthlyReminderData(null);
            }
        };

        checkMonthlyReminder();
    }, [currentUser]);

    // Trigger iOS scroll fix after data loads
    useEffect(() => {
        if (!loading) {
            // Trigger scroll fix after content loads
            const timeouts = [
                setTimeout(() => forceIOSScrollReset(), 150),
                setTimeout(() => forceIOSScrollReset(), 400),
                setTimeout(() => forceIOSScrollReset(), 800),
            ];
            
            return () => timeouts.forEach(clearTimeout);
        }
    }, [loading, forceIOSScrollReset]);

    // Additional effect to trigger scroll fix when timecards data changes
    useEffect(() => {
        if (timecards.length > 0) {
            // Data has loaded, ensure proper scroll position
            const timeoutId = setTimeout(() => {
                forceIOSScrollReset();
            }, 200);
            
            return () => clearTimeout(timeoutId);
        }
    }, [timecards, forceIOSScrollReset]);

    // Effect to trigger scroll fix when week info is set
    useEffect(() => {
        if (weekInfo) {
            // Week info has been set, ensure proper scroll position
            const timeoutId = setTimeout(() => {
                forceIOSScrollReset();
            }, 200);
            
            return () => clearTimeout(timeoutId);
        }
    }, [weekInfo, forceIOSScrollReset]);

    const handleCreateTimecard = async (project) => {
        if (!weekInfo) {
            setError('Week information not available');
            setIsCreateDialogOpen(false);
            return;
        }

        // Validate that we're creating a timecard for the current week
        const now = new Date();
        const currentWeekInfo = getWeekDates(now);
        console.log('handleCreateTimecard - validation:', {
            current: {
                startDate: currentWeekInfo.startDate.toISOString(),
                endDate: currentWeekInfo.endDate.toISOString()
            },
            selected: {
                startDate: weekInfo.startDate.toISOString(),
                endDate: weekInfo.endDate.toISOString()
            }
        });

        if (weekInfo.startDate.getTime() !== currentWeekInfo.startDate.getTime() ||
            weekInfo.endDate.getTime() !== currentWeekInfo.endDate.getTime()) {
            setError('Can only create timecards for the current week');
            setIsCreateDialogOpen(false);
            return;
        }

        // Check if project already has a timecard for this week
        if (timecards.some(tc => tc.project._id === project._id)) {
            setError('A timecard already exists for this project this week');
            setIsCreateDialogOpen(false);
            return;
        }

        try {
            // Create array of days from Sunday to Saturday
            const days = [];
            let currentDate = new Date(Date.UTC(
                weekInfo.startDate.getUTCFullYear(),
                weekInfo.startDate.getUTCMonth(),
                weekInfo.startDate.getUTCDate(),
                0, 0, 0, 0
            ));
            
            for (let i = 0; i < 7; i++) {
                days.push({
                    date: currentDate.toISOString(),
                    hours: 0
                });
                currentDate = new Date(Date.UTC(
                    currentDate.getUTCFullYear(),
                    currentDate.getUTCMonth(),
                    currentDate.getUTCDate() + 1,
                    0, 0, 0, 0
                ));
            }

            const timecardData = {
                projectId: project._id,
                startDate: weekInfo.startDate.toISOString(),
                endDate: weekInfo.endDate.toISOString(),
                days
            };

            await createTimecard(timecardData);
            // Check for uncertified timecards and update the banner state
            await checkUncertifiedTimecards();
            setIsCreateDialogOpen(false);
            setError(null);
        } catch (err) {
            console.error('Error creating timecard:', err);
            setError(err.message);
        }
    };

    const handleDeleteTimecard = async (timecardId) => {
        try {
            await deleteTimecard(timecardId);
            // Check for uncertified timecards and update the banner state
            await checkUncertifiedTimecards();
            setError(null);
        } catch (err) {
            console.error('Error deleting timecard:', err);
            setError(err.message);
        }
    };

    const handleEditTimecard = (timecard) => {
        setSelectedTimecard(timecard);
        setIsEditDialogOpen(true);
    };

    const handleSaveTimecard = async (updatedTimecard) => {
        try {
            const savedTimecard = await updateTimecard(updatedTimecard._id, {
                days: updatedTimecard.days
            });
            
            setTimecards(prev => prev.map(tc => 
                tc._id === savedTimecard._id ? savedTimecard : tc
            ));
            
            setIsEditDialogOpen(false);
            setSelectedTimecard(null);
            setError(null);
            setSuccess('Your timecard was successfully updated.');
        } catch (err) {
            console.error('Error updating timecard:', err);
            setError(err.message);
        }
    };

    const handleSubmitTimecard = async (updatedTimecard) => {
        try {
            setLoading(true);
            if (!selectedTimecard) {
                throw new Error('No timecard selected for submission');
            }
            
            console.log('Before submit - timecard:', {
                id: selectedTimecard._id,
                status: selectedTimecard.status,
                days: selectedTimecard.days.map(d => ({
                    date: d.date,
                    certified: d.certified,
                    submitted: d.submitted
                }))
            });
            
            // Check certification status to determine the appropriate message
            const totalDays = updatedTimecard.days.length;
            const certifiedDays = updatedTimecard.days.filter(d => d.certified && d.submitted).length;
            
            let successMessage;
            if (certifiedDays === totalDays) {
                successMessage = 'Your timecard was certified and submitted successfully.';
            } else {
                successMessage = 'Your timecard was partially certified and submitted successfully.';
            }
            
            // Update timecards list with the submitted timecard
            setTimecards(prev => prev.map(tc => 
                tc._id === updatedTimecard._id ? updatedTimecard : tc
            ));
            
            console.log('After submit - timecard:', {
                id: updatedTimecard._id,
                status: updatedTimecard.status,
                days: updatedTimecard.days.map(d => ({
                    date: d.date,
                    certified: d.certified,
                    submitted: d.submitted
                }))
            });
            
            // Re-check uncertified timecards to update the view appropriately
            await checkUncertifiedTimecards();
            
            setIsEditDialogOpen(false);
            setSelectedTimecard(null);
            setSuccess(successMessage);
        } catch (err) {
            console.error('Error after timecard submission:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getUsedProjectIds = () => {
        return timecards.map(timecard => timecard.project._id);
    };

    const handleErrorClose = () => {
        setError(null);
    };

    // Auto-clear success message after 20 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess('');
            }, 20000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={handleErrorClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            {showMonthlyReminder && monthlyReminderData && (
                <Alert 
                    severity="info" 
                    sx={{ 
                        mb: 3, 
                        fontWeight: 'bold', 
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        '& .MuiAlert-icon': {
                            marginTop: '2px',
                            marginRight: '12px',
                            flexShrink: 0
                        },
                        '& .MuiAlert-message': {
                            paddingTop: '2px',
                            flex: 1,
                            minWidth: 0
                        }
                    }}
                >
                    You have currently worked {monthlyReminderData.ytdUsed} hours on your project. There are {monthlyReminderData.remaining} hours remaining to meet the ECI mandated 1880 hours worked for the year.
                </Alert>
            )}

            {hasUncertifiedTimecards && shouldShowNotificationBanner() && (
                <Alert 
                    severity="warning" 
                    sx={{ mb: 3 }}
                >
                    Remember to certify your timecards no later than Monday by 8:00 AM.
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3, backgroundColor: '#e5f2e7' }}>
                    {success}
                </Alert>
            )}

            <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: isMobile ? 'flex-start' : 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? 2 : 0,
                mb: 3
            }}>
                <Typography variant="h6" sx={{ mb: isMobile ? 1 : 0 }}>
                    Week {weekInfo?.weekNum}: {weekInfo?.dateRange}
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row', // Keep horizontal on all devices
                    gap: isMobile ? 1 : 2, // Smaller gap on mobile
                    width: isMobile ? '100%' : 'auto',
                    flexWrap: isMobile ? 'wrap' : 'nowrap', // Allow wrapping if needed on mobile
                }}>
                    <Button
                        variant="contained"
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                            padding: isMobile ? '6px 8px' : '6px 16px',
                            minHeight: isMobile ? '32px' : '36px',
                            whiteSpace: 'nowrap',
                            flex: isMobile ? '1' : 'none', // Equal width on mobile
                            minWidth: isMobile ? '0' : 'auto', // Allow shrinking on mobile
                        }}
                        onClick={async () => {
                            try {
                                // Get current user data
                                const userData = await getCurrentUser();
                                
                                // Get calculated project hours for the current year
                                const projectHours = await getUserProjectHours(userData._id);
                                
                                setProjectHoursData({
                                    yearStart: projectHours.yearStart.toString(),
                                    ytdUsed: projectHours.ytdUsed.toString(),
                                    remaining: projectHours.remaining.toString()
                                });
                                setIsProjectHoursDialogOpen(true);
                            } catch (err) {
                                console.error('Error fetching project hours data:', err);
                                setError('Failed to fetch project hours data');
                            }
                        }}
                    >
                        {isMobile ? 'ECI Hrs' : 'ECI Hours'}
                    </Button>
                    <Button
                        variant="contained"
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                            padding: isMobile ? '6px 8px' : '6px 16px',
                            minHeight: isMobile ? '32px' : '36px',
                            whiteSpace: 'nowrap',
                            flex: isMobile ? '1' : 'none', // Equal width on mobile
                            minWidth: isMobile ? '0' : 'auto', // Allow shrinking on mobile
                        }}
                        onClick={async () => {
                            try {
                                // Get current user data for yearStart and ytdAccumulated
                                const userData = await getCurrentUser();
                                
                                // Get calculated PTO hours for the current year
                                const ptoHours = await getUserPtoHours(userData._id);
                                
                                setPTOData({
                                    yearStart: ptoHours.yearStart.toString(),
                                    ytdAccumulated: ptoHours.ytdAccumulated.toString(),
                                    ytdUsed: ptoHours.ytdUsed.toString(),
                                    available: ptoHours.available.toString()
                                });
                                setIsPTODialogOpen(true);
                            } catch (err) {
                                console.error('Error fetching PTO data:', err);
                                setError('Failed to fetch PTO data');
                            }
                        }}
                    >
                        PTO
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isMobile ? <AddIcon fontSize="small" /> : <AddIcon />}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                            padding: isMobile ? '6px 8px' : '6px 16px',
                            minHeight: isMobile ? '32px' : '36px',
                            whiteSpace: 'nowrap',
                            flex: isMobile ? '1' : 'none', // Equal width on mobile
                            minWidth: isMobile ? '0' : 'auto', // Allow shrinking on mobile
                        }}
                        onClick={() => setIsCreateDialogOpen(true)}
                        disabled={false} // Users can create multiple timecards for different projects
                    >
                        {isMobile ? 'Create' : 'Create Timecard'}
                    </Button>
                </Box>
            </Box>

            {timecards.map((timecard) => {
                const timecardWeekInfo = getWeekDates(parseUTCDate(timecard.startDate));
                const totalHours = timecard.days.reduce((sum, day) => sum + parseFloat(day.hours), 0);
                
                if (isMobile) {
                    // Mobile layout: transposed table (headers as first column, data as second column)
                    return (
                        <TableContainer 
                            component={Paper} 
                            key={timecard._id}
                            sx={{ mb: 3 }}
                        >
                            <Table size="small">
                                <TableBody>
                                    {/* User row */}
                                    <TableRow>
                                        <TableCell sx={{ 
                                            fontWeight: 'bold', 
                                            width: '40%',
                                            backgroundColor: '#e0e0e0',
                                            borderRight: '1px solid #bdbdbd',
                                            borderBottom: '1px solid #bdbdbd'
                                        }}>
                                            User
                                        </TableCell>
                                        <TableCell sx={{ 
                                            borderBottom: '1px solid #e0e0e0'
                                        }}>
                                            {currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown User'}
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Project row */}
                                    <TableRow>
                                        <TableCell sx={{ 
                                            fontWeight: 'bold',
                                            backgroundColor: '#e0e0e0',
                                            borderRight: '1px solid #bdbdbd',
                                            borderBottom: '1px solid #bdbdbd',

                                        }}>
                                            Project
                                        </TableCell>
                                        <TableCell sx={{ 

                                            borderBottom: '1px solid #e0e0e0'
                                        }}>
                                            {timecard.project.name}
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Week row */}
                                    <TableRow>
                                        <TableCell sx={{ 
                                            fontWeight: 'bold',
                                            backgroundColor: '#e0e0e0',
                                            borderRight: '1px solid #bdbdbd',
                                            borderBottom: '1px solid #bdbdbd',

                                        }}>
                                            Week
                                        </TableCell>
                                        <TableCell sx={{ 
                                            borderBottom: '1px solid #e0e0e0'
                                        }}>
                                            {timecardWeekInfo.dateRange}
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Day rows */}
                                    {timecardWeekInfo.weekDates.map((weekDay) => {
                                        const day = timecard.days.find(d => 
                                            parseUTCDate(d.date).getTime() === weekDay.full.getTime()
                                        );
                                        return (
                                            <TableRow key={weekDay.full.toISOString()}>
                                                <TableCell sx={{ 
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#e0e0e0',
                                                    borderRight: '1px solid #bdbdbd',
                                                    borderBottom: '1px solid #bdbdbd',

                                                }}>
                                                    {weekDay.dayName} {weekDay.display}
                                                </TableCell>
                                                <TableCell 
                                                    sx={{
                                                        bgcolor: (day?.certified && day?.submitted) ? 'action.hover' : 'inherit',

                                                        borderBottom: '1px solid #e0e0e0'
                                                    }}
                                                >
                                                    {day ? parseFloat(day.hours).toFixed(2) : '0.00'}
                                                    {day?.comments && (
                                                        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                                                            {day.comments}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    
                                    {/* Total row */}
                                    <TableRow>
                                        <TableCell sx={{ 
                                            fontWeight: 'bold',
                                            backgroundColor: '#e0e0e0',
                                            borderRight: '1px solid #bdbdbd',
                                            borderBottom: '1px solid #bdbdbd',

                                        }}>
                                            Total
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontWeight: 'bold',

                                            borderBottom: '1px solid #e0e0e0'
                                        }}>
                                            {totalHours.toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Actions row - merged cells with light blue background */}
                                    <TableRow>
                                        <TableCell 
                                            colSpan={2}
                                            sx={{ 
                                                backgroundColor: '#e3f2fd',
                                                textAlign: 'center',
                                                padding: '12px',
                                                borderTop: '1px solid #e0e0e0'
                                            }}
                                        >
                                            <IconButton
                                                onClick={() => handleEditTimecard(timecard)}
                                                size="small"
                                                sx={{ mr: 2 }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteTimecard(timecard._id)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }
                
                // Desktop layout: original horizontal table
                return (
                    <TableContainer 
                        component={Paper} 
                        key={timecard._id}
                        sx={{ mb: 3 }}
                    >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '15%' }}>User</TableCell>
                                <TableCell sx={{ width: '20%' }}>Project</TableCell>
                                <TableCell sx={{ width: '15%' }}>Week</TableCell>
                                {(() => {
                                    const timecardWeekInfo = getWeekDates(parseUTCDate(timecard.startDate));
                                    return timecardWeekInfo.weekDates.map((weekDay, index) => (
                                        <TableCell key={weekDay.full.toISOString()} align="center" sx={{ width: '7%' }}>
                                            {weekDay.dayName}<br />
                                            {weekDay.display}
                                        </TableCell>
                                    ));
                                })()}
                                <TableCell align="center">Total</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown User'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{timecard.project.name}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {(() => {
                                        const weekDates = getWeekDates(parseUTCDate(timecard.startDate));
                                        return `${weekDates.dateRange}`;
                                    })()}
                                </TableCell>
                                {(() => {
                                    const timecardWeekInfo = getWeekDates(parseUTCDate(timecard.startDate));
                                    return timecardWeekInfo.weekDates.map((weekDay) => {
                                    const day = timecard.days.find(d => 
                                        parseUTCDate(d.date).getTime() === weekDay.full.getTime()
                                    );
                                    return (
                                        <TableCell 
                                            key={weekDay.full.toISOString()} 
                                            align="center"
                                            sx={{
                                                bgcolor: (day?.certified && day?.submitted) ? 'action.hover' : 'inherit'
                                            }}
                                        >
                                            {day ? parseFloat(day.hours).toFixed(2) : '0.00'}
                                        </TableCell>
                                    );
                                    });
                                })()}
                                <TableCell align="center">
                                    {timecard.days.reduce((sum, day) => sum + parseFloat(day.hours), 0).toFixed(2)}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <IconButton
                                        onClick={() => handleEditTimecard(timecard)}
                                        size="small"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleDeleteTimecard(timecard._id)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                            {/* Comments row */}
                            {timecard.days.some(day => day.comments) && (
                                <TableRow>
                                    <TableCell colSpan={3} />
                                    {(() => {
                                        const timecardWeekInfo = getWeekDates(parseUTCDate(timecard.startDate));
                                        return timecardWeekInfo.weekDates.map((weekDay) => {
                                        const day = timecard.days.find(d => 
                                            parseUTCDate(d.date).getTime() === weekDay.full.getTime()
                                        );
                                        return (
                                            <TableCell 
                                                key={weekDay.full.toISOString()} 
                                                align="center"
                                                sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary',
                                                    bgcolor: (day?.certified && day?.submitted) ? 'action.hover' : 'inherit'
                                                }}
                                            >
                                                {day?.comments || ''}
                                            </TableCell>
                                        );
                                        });
                                    })()}
                                    <TableCell colSpan={2} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                );
            })}

            <CreateTimecardDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                currentUser={currentUser}
                onCreateTimecard={handleCreateTimecard}
                usedProjectIds={getUsedProjectIds()}
            />

            <EditTimecardDialog
                open={isEditDialogOpen}
                onClose={() => {
                    setIsEditDialogOpen(false);
                    setSelectedTimecard(null);
                    // Clear any success message when dialog is closed manually
                    setSuccess('');
                }}
                timecard={selectedTimecard}
                onSave={handleSaveTimecard}
                onSubmit={handleSubmitTimecard}
            />

            <PTODialog
                open={isPTODialogOpen}
                onClose={() => setIsPTODialogOpen(false)}
                ptoData={ptoData}
            />

            <ProjectHoursDialog
                open={isProjectHoursDialogOpen}
                onClose={() => setIsProjectHoursDialogOpen(false)}
                projectHoursData={projectHoursData}
            />
        </Box>
    );
};

export default Timecard;
